<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps<{ modelValue: string }>();
const emit  = defineEmits<{ (e: 'update:modelValue', val: string): void }>();

const countries = [
  { code: 'KE', dial: '+254', flag: '🇰🇪' },
  { code: 'UG', dial: '+256', flag: '🇺🇬' },
  { code: 'TZ', dial: '+255', flag: '🇹🇿' },
  { code: 'NG', dial: '+234', flag: '🇳🇬' },
  { code: 'ZA', dial: '+27',  flag: '🇿🇦' },
];

const dialCode   = ref('+254');
const phoneLocal = ref('');
const focused    = ref(false);

function parseValue(val: string) {
  if (!val) { phoneLocal.value = ''; return; }
  for (const c of countries) {
    if (val.startsWith(c.dial)) {
      dialCode.value   = c.dial;
      phoneLocal.value = val.slice(c.dial.length).replace(/^\s+/, '');
      return;
    }
  }
  phoneLocal.value = val;
}

parseValue(props.modelValue);

watch(() => props.modelValue, parseValue);

function emitCombined() {
  const local = phoneLocal.value.replace(/\D/g, '').replace(/^0+/, '');
  emit('update:modelValue', local ? dialCode.value + local : '');
}

const selectedCountry = () => countries.find(c => c.dial === dialCode.value) ?? countries[0];
</script>

<template>
  <div class="phone-field" :class="{ focused }">
    <!-- Country prefix — styled as left section of the field -->
    <div class="phone-prefix">
      <span class="phone-flag">{{ selectedCountry().flag }}</span>
      <select
        v-model="dialCode"
        class="phone-dial"
        @change="emitCombined"
        @focus="focused = true"
        @blur="focused = false"
        aria-label="Country code"
      >
        <option v-for="c in countries" :key="c.code" :value="c.dial">
          {{ c.flag }} {{ c.dial }}
        </option>
      </select>
      <span class="phone-dial-text">{{ dialCode }}</span>
      <svg class="phone-chevron" width="11" height="11" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>

    <div class="phone-divider"></div>

    <!-- Number input -->
    <input
      v-model="phoneLocal"
      type="tel"
      class="phone-number"
      placeholder="700 000 000"
      autocomplete="tel"
      @input="emitCombined"
      @focus="focused = true"
      @blur="focused = false"
    />
  </div>
</template>

<style scoped>
.phone-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 42px;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  background: white;
  transition: border-color 0.15s, box-shadow 0.15s;
  overflow: hidden;
}
.phone-field.focused {
  border-color: #0061eb;
  box-shadow: 0 0 0 3px rgba(0, 97, 235, 0.08);
}

/* Left prefix section */
.phone-prefix {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 10px 0 12px;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
}
.phone-flag { font-size: 1rem; line-height: 1; pointer-events: none; }
.phone-dial-text { font-size: 0.87rem; font-weight: 600; color: #374151; pointer-events: none; white-space: nowrap; }
.phone-chevron { color: #94a3b8; pointer-events: none; flex-shrink: 0; }

/* The real select — invisible, positioned over the prefix area */
.phone-dial {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
}

/* Divider */
.phone-divider {
  width: 1px;
  height: 22px;
  background: #e2e8f0;
  flex-shrink: 0;
}

/* Number input */
.phone-number {
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 12px;
  font-size: 0.87rem;
  color: #1e293b;
  background: transparent;
  font-family: inherit;
}
.phone-number::placeholder { color: #94a3b8; }
</style>
